<template>

    <kt-modal
        :visible="recordVisible"
        :title="recordTitle"
        @dismiss="onDismiss()"
        size="modal-lg"
    >
        <template #body>
            <part-form v-model="record"></part-form>
            
            <!-- 
            // moving to the partform ...
            Show attachments section only if part is saved (has an ID) 
            <div v-if="record.Id" class="mt-3">
                <part-attachment-form 
                    :part-id="record.Id" 
                    :request-id="record.RequestId"
                ></part-attachment-form>
            </div>
            -->
        </template>
    
        <template #footer>
            <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="sendNotificationTogglePart"
                        v-model="sendNotification"
                    >
                    <label class="form-check-label" for="sendNotificationTogglePart">
                        Send Update Notification
                    </label>
                </div>
                <div>
                    <button @click="onDismiss()" class="btn btn-outline-secondary">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary ms-2">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
        
    </kt-modal>
    
</template>

<script lang="ts" setup>

import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { PartModel } from "@/models/PartModel";
import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import KtModal from "@/components/UI/KtModal.vue";
import PartForm from "@/components/Parts/PartForm.vue";
import PartAttachmentForm from "@/components/Parts/PartAttachmentForm.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Part Entry");
const recordVisible = ref(true);

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    },
    requestId: {
        type: Number,
        default: 0
    },
    clientId: {
        type: Number,
        default: 0
    },
    techId: {
        type: Number,
        default: 0
    },
    embedded: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(["cancel", "saved"]);

const unwatch:Array <Function> = [];

const newRecord = () =>
{
    const rec = new PartModel();
    // Pre-populate with context if provided
    if (props.requestId) {
        rec.RequestId = props.requestId;
    }
    if (props.clientId) {
        rec.CustomerNumber = props.clientId;
    }
    if (props.techId) {
        rec.AssignedTo = props.techId;
    }
    // Set default NeededBy and WorkCompleteDate to today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    rec.NeededBy = todayStr;
    rec.WorkCompleteDate = todayStr;
    return rec;
};

const record = ref<PartModel>(newRecord());
const sendNotification = ref(false);

const saveRecord = async () =>
{
    try {
        let data = Helper.prep(record.value);
        
        // .. TODO ... validate record here


        console.debug("save", data);

        data = await ApiService.savePart(data);

        // If embedded mode or global modal mode (any context props provided), emit saved event
        const isGlobalModal = props.id !== null || props.requestId || props.clientId || props.techId;
        if (props.embedded || isGlobalModal) {
            emit("saved");
        } else {
            // redirect
            Emitter.emit("redirect", "/part/edit/" + data.Id);
        }
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () =>
{
    Alert.notify("Deletion is currently disabled.");
};

const onDismiss = () =>
{
    // If embedded mode or global modal mode (any context props provided), emit cancel event
    const isGlobalModal = props.id !== null || props.requestId || props.clientId || props.techId;
    if (props.embedded || isGlobalModal) {
        emit("cancel");
    } else {
        router.back();
    }
};

const loadRecord = async (val?: string | number | null) =>
{
    // Priority: id prop > embedded mode > route params
    if (props.id !== null && props.id !== undefined) {
        // Global modal mode with id prop
        if (props.id && props.id !== "new") {
            let rec = await ApiService.loadPart(props.id);
            console.debug("part", rec);
            record.value = rec;
        } else {
            record.value = newRecord();
        }
    } else if (props.embedded && props.requestId) {
        // In embedded mode, create a new part for the request
        let newRec = newRecord();
        newRec.RequestId = props.requestId;
        record.value = newRec;
    } else if (val) {
        // Route-based loading
        switch (val.toString())
        {
            case "new":
                record.value = newRecord();
                break;

            default:
                let rec = await ApiService.loadPart(val);
                console.debug("part", rec);
                record.value = rec;
                break;
        }
    } else {
        record.value = newRecord();
    }
};

// Watch id prop for global modal usage
unwatch.push(watch(() => props.id, async (newId) => {
    if (newId !== null && newId !== undefined) {
        await loadRecord();
    }
}));

const hasContext = !!(props.requestId || props.clientId || props.techId);
if (!props.embedded && props.id === null && !hasContext) {
    // Only watch route params if not in embedded mode, no id prop, and no context props
    unwatch.push(watch(() => route.params.id || route.name, async (nv, ov) => 
    {
        // If route name is "Part New" or id is undefined, treat as new
        let idValue = route.params.id || (route.name === "Part New" ? "new" : "");
        if (Array.isArray(idValue)) {
            idValue = idValue[0];
        }
        loadRecord(idValue);
    }, 
    { 
        immediate: true 
    }));
} else if (hasContext && props.id === null) {
    // Global modal with context props — create new record with pre-populated fields
    record.value = newRecord();
} else if (props.id !== null) {
    // Load immediately when id prop is provided
    loadRecord();
} else if (props.embedded) {
    // Load immediately in embedded mode
    loadRecord("new");
}

onUnmounted(() => 
{
    unwatch.forEach((un) => 
    {
        un();
    });
});
    
</script>